import { createFileRoute } from "@tanstack/react-router";
import { MarketingShell } from "@/components/marketing-shell";
import { redirectIfAuthenticated } from "@/lib/marketing-guards";

export const Route = createFileRoute("/privacy")({
  beforeLoad: redirectIfAuthenticated,
  head: () => ({
    meta: [
      { title: "Privacy Policy, Super Affiliate OS" },
      {
        name: "description",
        content:
          "How Super Affiliate OS collects, uses, stores, and protects your personal and business data.",
      },
      { property: "og:title", content: "Privacy Policy, Super Affiliate OS" },
    ],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <MarketingShell>
      <article className="prose prose-invert mx-auto max-w-3xl px-6 py-20 prose-headings:tracking-tight prose-h1:text-4xl prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-3 prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground">
        <h1>Privacy Policy</h1>
        <p className="text-sm text-muted-foreground">Last updated: May 19, 2026</p>

        <p>
          We respect your privacy. This policy explains what data we collect, how we use it,
          and what choices you have.
        </p>

        <h2>What we collect</h2>
        <ul>
          <li><strong>Account data:</strong> name, email, password hash, profile photo.</li>
          <li><strong>Billing data:</strong> handled by Stripe. We never store full card numbers.</li>
          <li><strong>Workspace data:</strong> the CSVs, commissions, contacts, and notes you upload.</li>
          <li><strong>Usage data:</strong> pages visited, features used, errors encountered.</li>
          <li><strong>Device data:</strong> browser, OS, IP address, used for security and analytics.</li>
        </ul>

        <h2>How we use it</h2>
        <ul>
          <li>To provide, maintain, and improve the Service.</li>
          <li>To send essential account and billing emails.</li>
          <li>To send product updates (opt-out via Settings → Notifications).</li>
          <li>To detect abuse, fraud, and security incidents.</li>
          <li>To meet our legal obligations.</li>
        </ul>

        <h2>How we share it</h2>
        <p>
          We do not sell your data. We share it only with vetted infrastructure providers who
          power the Service, Stripe (payments), Resend (email), Lovable Cloud (hosting and
          database), and Sentry (error tracking), and only the minimum needed for each.
        </p>

        <h2>Security</h2>
        <p>
          Every row of your data is protected by Postgres row-level security policies scoped
          to your account. Data is encrypted in transit and at rest. We run regular security
          scans and log every administrative action.
        </p>

        <h2>Your rights</h2>
        <ul>
          <li><strong>Access:</strong> export your data any time as CSV.</li>
          <li><strong>Correction:</strong> edit your profile and workspace data in-app.</li>
          <li><strong>Deletion:</strong> close your account to schedule deletion (30-day retention window).</li>
          <li><strong>Portability:</strong> exports are open CSV and JSON.</li>
        </ul>

        <h2>Data retention</h2>
        <p>
          We retain your data while your account is active. After cancellation we keep it for
          30 days, then permanently delete. Backups are purged within 90 days.
        </p>

        <h2>International transfers</h2>
        <p>
          Our infrastructure may process data outside your home country. We use appropriate
          safeguards (standard contractual clauses where required) to keep data protected.
        </p>

        <h2>Children</h2>
        <p>
          The Service is not intended for anyone under 18. We don't knowingly collect data
          from minors.
        </p>

        <h2>Changes</h2>
        <p>
          Material changes to this policy will be announced by email or in-app notice.
        </p>

        <h2>Contact</h2>
        <p>
          Privacy questions? Email <strong>support@superaffiliateos.com</strong>.
        </p>
      </article>
    </MarketingShell>
  );
}
